<template>
    <div class="range-input form-control-container">
        <button
            type="button"
            class="btn input-addon"
            data-testid="btnMinus"
            :disabled="minReached"
            @click="handleMinus"
            tabindex="-1"
        >
            &minus;
        </button>

        <div class="form-control" :class="{'invalid': isInvalid}">
            <input
                ref="inputElement"
                type="text"
                data-testid="formField"
                :value="userValue"
                @input="handleValueChange"
                @keydown.enter="handleEnterKey"
                @keydown.esc="handleEscapeKey"
                @keydown.prevent.up="handlePlus"
                @keydown.prevent.down="handleMinus"
                @focus="handleFocus"
                @blur="handleBlur"
                data-lpignore="true"
            />
            <span class="unit" v-if="unit.length > 0">
                {{ $t(`unit.${unit}`) }}
            </span>
        </div>

        <button
            type="button"
            class="btn input-addon"
            data-testid="btnPlus"
            :disabled="maxReached"
            @click="handlePlus"
            tabindex="-1"
        >
            &plus;
        </button>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, PropType, ref } from 'vue'
import { useI18n } from 'vue-i18n';

export default defineComponent({
    props: {
        range: {
            type: null as unknown as PropType<number[]|null>,
            required: true,
            validator(this: void, prop: number[]|null) {
                return Array.isArray(prop) || prop === null;
            },
        },
        unit: {
            type: String,
            default: "",
        },
        default: {
            type: Number,
            required: true,
        },
        isFree: {
            type: Boolean,
            default: true,
        },
        decimals: {
            type: Number,
            default: 2,
        },
        modelValue: {
            type: null as unknown as PropType<number|null>,
            required: true,
            validator(this: void, prop: number|null) {
                return typeof prop === "number" || prop === null;
            },
        },
        absoluteMin: {
            type: null as unknown as PropType<number|null>,
            default: null,
        },
        absoluteMax: {
            type: null as unknown as PropType<number|null>,
            default: null,
        }
    },

    emits: {
        "update:modelValue": (payload: number) => !Number.isNaN(payload),
    },

    setup(props, {emit})
    {
        const i18n = useI18n();
        const inputElement = ref<InstanceType<typeof HTMLInputElement>>();

        const numericModelVal = computed<number>(() => {
            if (!props.modelValue) {
                return props.default;
            }

            return props.modelValue;
        });

        if (numericModelVal.value !== props.modelValue) {
            updateModelValue(props.default);
        }

        const userValue = ref(formatValue(numericModelVal.value));
        const isInvalid = ref(false);
        let oldValue = userValue.value;
        let userBeforeEditValue = userValue.value;

        // When the component is mounted to the DOM, perform an initial validation of the value
        onMounted(() => {
            updateFieldValidation(numericModelVal.value);
        });

        //
        // Various utility functions
        //

        function formatValue(value: number) : string
        {
            return value.toFixed(props.decimals);
        }

        function roundValue(value: number) : number
        {
            const roundMultiplier = Math.pow(10, props.decimals);

            return Math.round(value * roundMultiplier) / roundMultiplier;
        }

        function getNextValue(value: number) : number|null
        {
            if (props.range === null) {
                return Math.floor(value + 1);
            }

            for (let i = 0; i < props.range.length; i++) {
                const tmpValue = roundValue(props.range[i]);
                
                if (tmpValue > value) {
                    return tmpValue;
                }
            }

            return null;
        }

        function getPreviousValue(value: number) : number|null
        {
            if (props.range === null) {
                return Math.ceil(value - 1);
            }

            for (let i = props.range.length - 1; i >= 0; i--) {
                const tmpValue = roundValue(props.range[i]);

                if (tmpValue < value) {
                    return tmpValue;
                }
            }

            return null;
        }

        function getNearestValue(value: number) : number
        {
            if (props.range !== null) {
                // If there is an exact value matching this one, return it
                for (let i = 0; i < props.range.length; i++) {
                    const tmpValue = roundValue(props.range[i]);
    
                    if (value === tmpValue) {
                        return value;
                    }
                }
            }

            // Otherwise, find out which one from the next and the previous is the closest
            const nextValue = getNextValue(value);
            const prevValue = getPreviousValue(value);

            // No luck, this should never happen but just in case, return the default value...
            if (nextValue === null && prevValue === null) {
                return props.default;
            }

            // If there is either no next or previous value, return the other one available
            if (prevValue === null) {
                // Typescript bug? Needs to cast in "number" because it cannot see that nextValue cannot be null here
                return nextValue as number;
            }

            if (nextValue === null) {
                return prevValue;
            }

            // If both are available, return the closest one
            return (Math.abs(prevValue - value) < Math.abs(nextValue - value)) ? prevValue : nextValue;
        }

        function restoreInputOldValue()
        {
            // Looks like a bug: when setting a value, trying to set the same oldValue twice
            // doesn't update the DOM anymore, even if userValue.value was modified by the
            // user. Setting another random value fixes it...
            userValue.value += " ";
            userValue.value = oldValue;
        }

        function forceInputValue(value: string)
        {
            oldValue = value;
            restoreInputOldValue();
        }

        function updateFieldValidation(value: number)
        {
            if (!inputElement.value) {
                return;
            }

            if (props.absoluteMin !== null && value < props.absoluteMin) {
                isInvalid.value = true;
                inputElement.value.setCustomValidity(i18n.t("validationMessage.min"));
                return;
            }

            if (props.absoluteMax !== null && value > props.absoluteMax) {
                isInvalid.value = true;
                inputElement.value.setCustomValidity(i18n.t("validationMessage.max"));
                return;
            }

            isInvalid.value = false;
            inputElement.value.setCustomValidity("");
        }

        function updateModelValue(value: number)
        {
            emit("update:modelValue", value);
        }

        //
        // Event handlers
        //

        function handlePlus()
        {
            const nextValue = getNextValue(numericModelVal.value);

            if (nextValue !== null) {
                forceInputValue(formatValue(nextValue));
                updateFieldValidation(nextValue);
                updateModelValue(nextValue);
            }
        }

        function handleMinus()
        {
            const previousValue = getPreviousValue(numericModelVal.value);

            if (previousValue !== null) {
                forceInputValue(formatValue(previousValue));
                updateFieldValidation(previousValue);
                updateModelValue(previousValue);
            }
        }

        function handleFocus()
        {
            userBeforeEditValue = userValue.value;

            // When the value is zero, the user probably wants to type a different number without clearing the field
            if (numericModelVal.value === 0) {
                forceInputValue("");
            }
        }

        function handleValueChange(e: Event)
        {
            const input = e.target as HTMLInputElement|null;

            if (input === null) {
                return;
            }

            // Allow to clear the field to type a new value
            if (input.value === "") {
                forceInputValue("");
                return;
            }

            // User may enter a "plus" or a "minus" sign at the beginning of the number being typed
            if (input.value === "+" || input.value === "-") {
                forceInputValue(input.value);
                return;
            }

            // Only allow the user to type a numeric value
            if (!/^(\+|-)?[0-9]+(\.([0-9]+)?)?$/.test(input.value)) {
                restoreInputOldValue();
                return;
            }

            // The typed value is valid and can be remembered.
            oldValue = input.value;
            userValue.value = oldValue;
        }

        function handleBlur()
        {
            // If the user tried to clear the field but didn't type a new value,
            // restore the value it had before it was edited.
            if (["", "+", "-"].includes(userValue.value)) {
                forceInputValue(userBeforeEditValue);
                return;
            }

            let numericValue = roundValue(parseFloat(userValue.value));

            if (!props.isFree) {
                numericValue = getNearestValue(numericValue);
            }

            // Handle validation
            updateFieldValidation(numericValue);

            // Round and fix decimals
            forceInputValue(formatValue(numericValue));

            // Emit the updated value
            updateModelValue(numericValue);
        }

        function handleEnterKey()
        {
            // When the user hit the enter key, commit the typed value to the model (same as blur, but don't remove
            // the focus on the element, otherwise the parent form (if any) will not be submitted)
            handleBlur();
        }

        function handleEscapeKey(e: KeyboardEvent)
        {
            const input = e.target as HTMLInputElement|null;

            // Restore the value that was set before trying to edit it and un-focus the element
            if (input !== null) {
                forceInputValue(userBeforeEditValue);
                input.blur();
            }
        }

        //
        // Computed properties to get whether the min or max is reached
        //
        const minReached = computed(() => {
            if (props.range === null) {
                return false;
            }

            if (props.range.length < 1) {
                return true;
            }

            return numericModelVal.value <= roundValue(props.range[0]);
        });

        const maxReached = computed(() => {
            if (props.range === null) {
                return false;
            }

            if (props.range.length < 1) {
                return true;
            }

            return numericModelVal.value >= roundValue(props.range[props.range.length - 1]);
        });

        return {
            inputElement,
            userValue,
            isInvalid,
            handlePlus,
            handleMinus,
            handleFocus,
            handleValueChange,
            handleBlur,
            handleEnterKey,
            handleEscapeKey,
            minReached,
            maxReached,
        };
    },
})
</script>

<style lang="scss" scoped>

.range-input {
    display: flex;

    & > .form-control {
        display: flex;
        align-items: center;
        padding: 0 0.65rem 0 0;

        & > input {
            flex: 1;
            padding: 0.75rem 0.3rem 0.75rem 0.65rem;
            border: none !important;
            outline: none !important;
            text-align: right;
        }

        &:focus-within {
            padding: 0 calc(0.65rem - 1px) 0 0;
        }
        &:focus-within > input {
            padding: calc(0.75rem - 1px) 0.3rem calc(0.75rem - 1px) calc(0.65rem - 1px);
        }
    }

    & > .btn {
        padding: 0 0.75rem;
        font-size: 2rem;
    }
}

</style>
